/**
 * Lazy-loads size chart CSS/JS and mounts the modal shell on first interaction with a trigger.
 * Re-dispatches the activating click so the modal opens without a second user action.
 */
(function () {
  'use strict';

  const TRIGGER_SELECTOR = '[aria-controls="size-chart-pdp"]';
  const TEMPLATE_ID = 'size-chart-modal-template';

  const scriptEl = document.currentScript;
  const assets = {
    css: scriptEl?.dataset.css || '',
    js: scriptEl?.dataset.js || '',
  };

  let loadPromise = null;
  let isReady = false;

  /**
   * @param {EventTarget|null} target
   * @returns {Element|null}
   */
  function findTrigger(target) {
    return target instanceof Element ? target.closest(TRIGGER_SELECTOR) : null;
  }

  /** @param {string} href */
  function loadStylesheet(href) {
    if (!href || document.querySelector(`link[href="${href}"]`)) return;

    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = href;
    document.head.appendChild(link);
  }

  /**
   * @param {string} src
   * @returns {Promise<void>}
   */
  function loadScript(src) {
    return new Promise((resolve, reject) => {
      if (!src || document.querySelector(`script[src="${src}"]`)) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`[size-chart-loader] failed to load script: ${src}`));
      document.head.appendChild(script);
    });
  }

  function mountModalShell() {
    if (document.getElementById('size-chart-pdp')) return;

    const template = document.getElementById(TEMPLATE_ID);
    if (template instanceof HTMLTemplateElement) {
      document.body.appendChild(template.content.cloneNode(true));
    }
  }

  /** @returns {Promise<void>} */
  function loadSizeChartAssets() {
    if (isReady) return Promise.resolve();

    if (!loadPromise) {
      loadPromise = (async () => {
        loadStylesheet(assets.css);
        mountModalShell();
        await loadScript(assets.js);
        await customElements.whenDefined('size-charts');
        isReady = true;
      })().catch((error) => {
        loadPromise = null;
        throw error;
      });
    }

    return loadPromise;
  }

  /** @param {MouseEvent} event */
  function handleClick(event) {
    const trigger = findTrigger(event.target);
    if (!trigger || isReady) return;

    event.preventDefault();
    event.stopImmediatePropagation();

    loadSizeChartAssets()
      .then(() => trigger.click())
      .catch((error) => console.error(error));
  }

  document.addEventListener('click', handleClick, true);
})();
